import { useContext, useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import environment from '../utils/environment';
import currentPartner from '../constants/partners';
import { AppConfigContext } from '../contexts/appConfigContext';

// Keep track of RUM internal state to prevent double initialization
let isInitialized = false;
// Intentionally left empty; populate with known noisy/benign error messages to ignore in Datadog RUM.
const IGNORED_ERRORS: string[] = [];

const PROVIDER_NAME_PATTERN =
  /\b(?:(?:Dr|Doctor|Nurse|Physician|Clinician)\.?\s+)?\p{L}+(?:[-'’]\p{L}+)*(?:\s+\p{L}+(?:[-'’]\p{L}+)*){1,2}\s+(?:(?:GP|MD|RN|NP|DO|PA)(?:\/(?:GP|MD|RN|NP|DO|PA))*|Nurse|Physician|Doctor|Clinician)\b/giu;

const buildVersion = process.env.REACT_APP_BUILD_NUMBER || 'dev';
const customDatadogEnv = process.env.REACT_APP_DATADOG_ENV;

const VALID_ENV_TAGS = [
  'prod-fr',
  'prod',
  'test-demo',
  'test-no-growth',
  'test-qandm',
  'test-fr-care',
  'test-paw',
] as const;

const getEnvTag = () => {
  try {
    const source = customDatadogEnv || window.location.hostname;
    if (environment.IS_PROD) {
      return source.includes('livi.fr') || source.includes('kry.fr')
        ? 'prod-fr'
        : 'prod';
    }
    const testTag = VALID_ENV_TAGS.find(
      (tag) => tag.startsWith('test-') && source.includes(tag)
    );
    return testTag ?? 'unknown';
  } catch (error) {
    return 'unknown';
  }
};

const envTag = getEnvTag();

const getWebApp = (): string => {
  if (currentPartner) return currentPartner.id.toUpperCase();
  const { hostname } = window.location;
  return hostname.includes('livi.fr') || hostname.includes('livi.co.uk')
    ? 'LIVI'
    : 'KRY';
};

// even if permissions are granted, we only want to track in prod and test-demo envs to avoid noise from internal users in other envs
// DATADOG_ENV override also enables tracking for local proxy testing against a specific env
const isAllowedEnv =
  environment.IS_PROD ||
  envTag === 'test-demo' ||
  envTag === 'test-no-growth' ||
  (!!customDatadogEnv && envTag !== 'unknown');

const config = {
  applicationId: 'fc176bf9-c799-4214-81c5-e63257210e99',
  clientToken: 'pub8b8e47654757ac004be65581457c63b5',
  site: 'datadoghq.eu',
  service: 'patient-web-app',
  env: envTag,
  trackingConsent: 'not-granted' as const,
  trackAnonymousUser: false,
  // trackViewsManually: true,
  trackUserInteractions: isAllowedEnv,
  sessionSampleRate: isAllowedEnv ? 100 : 0,
  sessionReplaySampleRate: isAllowedEnv ? 10 : 0,
  trackResources: true,
  version: buildVersion,
  allowedTracingUrls: [
    /https:\/\/.*\.kry\.se/,
    'https://app.kry.se',
    /https:\/\/.*\.kry\.no/,
    'https://app.kry.no',
    /https:\/\/.*\.kry\.fr/,
    'https://app.kry.fr',
    /https:\/\/.*\.livi\.fr/,
    'https://app.livi.fr',
    /https:\/\/.*\.kry\.co\.uk/,
    'https://app.kry.co.uk',
    /https:\/\/.*\.livi\.co\.uk/,
    'https://app.livi.co.uk',
  ],
  beforeSend: (event: any) => {
    if (event.type === 'error') {
      // OpenTok logs this to console.error when the signalling socket closes without
      // a proper close code, most commonly during multi tab takeover (the server
      // force closes the yielding tabs socket with code 4020). It cannot be caught
      // via the OpenTok event API so we drop it here to avoid noise in RUM metrics.
      const errorMessage = event.error?.message ?? '';
      if (/RumorSocket.*Error code unavailable/i.test(errorMessage)) {
        return false;
      }

      // OpenTok SDK uses console.error('%cOpenTok:...') for its internal debug
      // logging. These are captured by the RUM SDK as errors but are not
      // actionable from our side — drop them all to reduce noise.
      if (errorMessage.startsWith('%cOpenTok:')) {
        return false;
      }

      // Cross-origin Script errors from browser-injected scripts (Brave shields,
      // Firefox iOS extensions, WKWebView content blockers). All app scripts are
      // same-origin so these are never from our code.
      if (errorMessage.includes('Script error.')) {
        return false;
      }

      if (IGNORED_ERRORS.some((err) => errorMessage.includes(err))) {
        return false;
      }

      const statusCode = event.error?.resource?.status_code;
      if (statusCode === 401 || errorMessage.includes('Not logged in')) {
        return false;
      }
    }
    if (event.type === 'action' && event.action?.target?.name) {
      const sanitized = event.action.target.name
        .replace(
          /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
          '[id]'
        )
        .replace(/\([^)]+\)/g, '(...)')
        .replace(PROVIDER_NAME_PATTERN, '[provider]')
        .replace(
          /\bLicensed\s+(?:physician|clinician|therapist)\b/gi,
          '[provider-title]'
        );
      const parts = sanitized.split(/[·\n\r]/);
      event.action.target.name =
        parts.length > 1 ? `${parts[0].trim()}...` : sanitized.trim();
    }
    return true;
  },
};

const ifInitialized =
  <T extends unknown[]>(fn: (...args: T) => void) =>
  (...args: T): void => {
    if (!isInitialized) return;
    fn(...args);
  };

const useRumTracker = () => {
  const { userId } = useContext(AppConfigContext);

  const initialize = () => {
    if (isInitialized) {
      return;
    }
    datadogRum.init(config);
    isInitialized = true;
  };

  const enableTracking = () => {
    initialize();
    datadogRum.setTrackingConsent('granted');
    datadogRum.setUser({ id: userId ?? 'anonymous' });
    datadogRum.setGlobalContextProperty('web.app', getWebApp());
    datadogRum.startSessionReplayRecording();
  };

  const disableTracking = ifInitialized(() => {
    datadogRum.setTrackingConsent('not-granted');
    datadogRum.stopSessionReplayRecording();
    datadogRum.clearUser();
    datadogRum.removeGlobalContextProperty('meeting.id');
    try {
      datadogRum.stopSession();
    } catch (error) {}
  });

  const startView = ifInitialized((name: string) => {
    datadogRum.startView({ name });
  });

  const startSessionReplayRecording = ifInitialized(() => {
    datadogRum.startSessionReplayRecording();
  });

  const stopSessionReplayRecording = ifInitialized(() => {
    datadogRum.stopSessionReplayRecording();
  });

  const addAction = ifInitialized((name: string, context?: object) => {
    datadogRum.addAction(name, context);
  });

  const addError = ifInitialized((error: Error, context?: object) => {
    datadogRum.addError(error, context);
  });

  const setMeetingId = ifInitialized((meetingId: string) => {
    datadogRum.setGlobalContextProperty('meeting.id', meetingId);
  });

  const clearMeetingId = ifInitialized(() => {
    datadogRum.removeGlobalContextProperty('meeting.id');
  });

  return useRef({
    enableTracking,
    disableTracking,
    startSessionReplayRecording,
    stopSessionReplayRecording,
    addAction,
    addError,
    startView,
    setMeetingId,
    clearMeetingId,
  }).current;
};

export default useRumTracker;
